import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import RootFractionInput, { parseRootFractionInput } from "../../../../components/inputs/RootFractionInput";
import { formatLatexPreview } from "../../../../components/inputs/RootFractionInput";


// 1. Generowanie danych

function simplifySqrt(n) {
  let outside = 1;
  let inside = n;
  for (let i = 2; i * i <= inside; i++) {
    while (inside % (i * i) === 0) {
      inside /= i * i;
      outside *= i;
    }
  }
  return { outside, inside };
}

function gcd(a, b) {
  while (b) [a, b] = [b, a % b];
  return Math.abs(a);
}

function formatDistanceAsSqrtFraction({ x, y, A, B, C }) {
  const numeratorVal = Math.abs(A * x + B * y + C);
  const denSqrVal = A * A + B * B;
  const sqrtDen = Math.sqrt(denSqrVal);

  if (Number.isInteger(sqrtDen)) {
    const divisor = gcd(numeratorVal, sqrtDen);
    const a = numeratorVal / divisor;
    const b = sqrtDen / divisor;
    const latex = b === 1 ? `${a}` : `\\dfrac{${a}}{${b}}`;
    return { latex, value: numeratorVal / sqrtDen };
  }

  const sqrt = simplifySqrt(denSqrVal);
  const outside = sqrt.outside;
  const inside = sqrt.inside;

  const fullNumerator = numeratorVal * outside;
  const fullDenominator = denSqrVal;

  const divisor = gcd(fullNumerator, fullDenominator);
  const num = fullNumerator / divisor;
  const den = fullDenominator / divisor;

  const sqrtLatex = `\\sqrt{${inside}}`;
  const latex = den === 1
    ? `${num === 1 ? "" : num}${sqrtLatex}`
    : `\\dfrac{${num === 1 ? "" : num}${sqrtLatex}}{${den}}`;

  return {
    latex,
    value: numeratorVal / sqrtDen,
  };
}


export function generateTask() {
  const rand = () => Math.floor(Math.random() * 11) - 5;
  let x, y, A, B, C;

  do {
    A = rand();
  } while (A === 0);
  do {
    B = rand();
  } while (B === 0);
  C = rand() * 2;

  x = rand();
  y = rand();

  const data = { x, y, A, B, C };
  const { latex } = formatDistanceAsSqrtFraction(data);

  return {
    type: "DistancePointLineTask",
    data,
    expectedAnswer: latex
  };
}


// 2. Polecenie
export function renderTask({ data }) {
  let { x, y, A, B, C } = data;

  const gcd = (a, b) => {
    while (b) [a, b] = [b, a % b];
    return Math.abs(a);
  };

  const gcd3 = (a, b, c) => gcd(gcd(a, b), c);
  const divisor = gcd3(A, B, C);
  if (divisor > 1) {
    A /= divisor;
    B /= divisor;
    C /= divisor;
  }

  const formatTerm = (coef, variable, isFirst = false) => {
    if (coef === 0) return "";
    if (coef === 1) return (isFirst ? "" : "+ ") + variable;
    if (coef === -1) return "- " + variable;
    return (coef > 0 && !isFirst ? "+ " : "") + coef + variable;
  };

  const terms = [];
  if (A !== 0) terms.push(formatTerm(A, "x", true));
  if (B !== 0) terms.push(formatTerm(B, "y", terms.length === 0));
  if (C !== 0) terms.push((C > 0 && terms.length > 0 ? "+ " : "") + C);

  const equation = terms.length ? `${terms.join(" ")} = 0` : "0 = 0";

  return (
    <>
      <p>
        Oblicz odległość punktu <InlineMath math={`P = (${x}, ${y})`} /> od
        prostej: <InlineMath math={equation} /></p>
    </>
  );
}



// 3. Input
export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <RootFractionInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      label="d"
    />
  );
}


// 4. Sprawdzanie

export function checkAnswer({ data }, userAnswer) {
  const { latex: expectedLatex, value: expectedValue } = formatDistanceAsSqrtFraction(data);

  const raw = typeof userAnswer === "object" ? userAnswer.raw : userAnswer;
  const parsed =
    typeof userAnswer === "object" && userAnswer.parsed != null
      ? userAnswer.parsed
      : parseRootFractionInput(raw);

  // ✅ 1. Porównanie numeryczne — najważniejsze!
  if (parsed != null && Math.abs(parsed - expectedValue) < 0.01) {
    return true;
  }

  // 🔁 2. Porównanie latex (rezerwowo)
  const normalize = (str) =>
    String(str)
      .replace(/\s/g, "")
      .replace(/\*/g, "")
      .replace(/sqrt\(([^)]+)\)/g, "√$1")
      .replace(/\\sqrt{([^}]+)}/g, "√$1")
      .replace(/\\dfrac{([^}]+)}{([^}]+)}/g, "$1/$2")
      .replace(/\\frac{([^}]+)}{([^}]+)}/g, "$1/$2")
      .replace(/[()]/g, "")
      .replace(/\\/g, "");

  return normalize(raw) === normalize(expectedLatex);
}


// 5. Odpowiedź
export function renderAnswer({ data }) {
  const { latex } = formatDistanceAsSqrtFraction(data);

  return (
    <div style={{ marginTop: "0.5rem" }}>
      <InlineMath math={`d = ${latex}`} />
    </div>
  );
}



export function renderUserAnswer(userAnswer) {
  const raw = typeof userAnswer === "object" ? userAnswer.raw : userAnswer;

  if (!raw || typeof raw !== "string" || raw.trim() === "") {
    return <i>(brak odpowiedzi)</i>;
  }

  return (
      <InlineMath math={`\\displaystyle ${formatLatexPreview(raw, "d")}`} />
  );
}



// 6. Wyjaśnienie
export function renderExplanation({ data }) {
  const { A, B, C, x, y } = data;
  const { latex, value } = formatDistanceAsSqrtFraction(data);

  const parenIfNeg = (n) => (n < 0 ? `(${n})` : n);

  const Ax = `${parenIfNeg(A)} \\cdot ${parenIfNeg(x)}`;
  const By = `${parenIfNeg(B)} \\cdot ${parenIfNeg(y)}`;
  const Cstr = `${parenIfNeg(C)}`;
  const A2 = `${parenIfNeg(A)}^2`;
  const B2 = `${parenIfNeg(B)}^2`;

  const AxVal = A * x;
  const ByVal = B * y;
  const fullNumerator = `${Ax} + ${By} + ${Cstr}`;
  const computedNumerator = Math.abs(AxVal + ByVal + C);
  const denominatorSquared = A * A + B * B;

  return (
    <div className="space-y-4">
      <div>
        <strong>Krok 1:</strong> Korzystamy ze wzoru na odległość punktu od prostej:
        <BlockMath math={`d = \\dfrac{|Ax + By + C|}{\\sqrt{A^2 + B^2}}`} />
      </div>

      <div>
        <strong>Krok 2:</strong> Podstawiamy dane do wzoru i obliczamy:
        <BlockMath math={`d = \\dfrac{\\left|${fullNumerator}\\right|}{\\sqrt{${A2} + ${B2}}}=`} />
        <BlockMath math={`= \\dfrac{${AxVal} + ${ByVal} + ${C}}{\\sqrt{${A * A} + ${B * B}}}=`} />
        <BlockMath math={`= \\dfrac{${computedNumerator}}{\\sqrt{${denominatorSquared}}}`} />
      </div>

      <div>
        <strong>Krok 3:</strong> Upraszczamy ułamek (jeśli to możliwe):
        <BlockMath math={`d = ${latex}`} />
      </div>

      <div>
        <strong>Krok 4:</strong> Wartość przybliżona:
        <BlockMath math={`d \\approx ${value.toFixed(2)}`} />
      </div>
    </div>
  );
}

